import { omit } from 'lodash';
import React, { FC, useEffect, useState } from 'react';

import { connect } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';

import { getComposeTitle } from '../../../components/Compose/helpers';
import BaseHeaderMobile from '../../../components/Header/BaseHeaderMobile';
import useAttachmentsUpload from '../../../hooks/attachments/useAttachmentUpload';
import useParamTopicId from '../../../hooks/router/params/useParamTopicId';
import useComposeMode from '../../../hooks/router/search/useComposeMode';
import useComposeType from '../../../hooks/router/search/useComposeType';
import useIsMobile from '../../../hooks/useIsMobile';

import { DialogId } from '../../../models/dialog';
import { DraftDataFormatted } from '../../../models/draft';
import { getCloseComposeUrl } from '../../../routes/urls/searchParams/compose';

import { getIsDraftChanged, mapping } from './helpers';
import { mapDispatchToProps, mapStateToProps } from './redux';
import { Props } from './types';

/**
 * Header for the compose form
 *
 * @param props                       Props passed to the component
 * @param props.draft                 Current state of the draft
 * @param props.isDefaultDraftChanged Whether the draft has been changed
 * @param props.nodeId                Message ID
 * @param props.nodeType              Node type
 * @param props.requestDraftClear     Request that the draft is cleared
 * @param props.topicId               The ID of the selected topic
 * @returns                           The component itself
 */
const ComposeHeader: FC<Props> = ({
  draft,
  isDefaultDraftChanged,
  nodeId,
  nodeType,
  requestDraftClear,
  topicId,
}) => {
  const { handleClearDraft } = useAttachmentsUpload(nodeType);
  const [initialDraft, setInitialDraft] = useState<DraftDataFormatted | null>(
    null,
  );
  const topicIdParam = useParamTopicId();

  useEffect(() => {
    if (draft?.nodeId === null || initialDraft?.nodeId === draft?.nodeId) {
      return;
    }

    setInitialDraft(draft);
  }, [draft]);

  // Due date not needed for templates
  const isTemplateDraftChanged =
    JSON.stringify(omit(draft, ['dateDueCurrent', 'dateDueSaved'])) !==
    JSON.stringify(omit(initialDraft, ['dateDueCurrent', 'dateDueSaved']));

  const isChanged =
    nodeType === 'MESSAGE' &&
    getIsDraftChanged(isDefaultDraftChanged, topicId, topicIdParam);

  const isMobile = useIsMobile();
  const location = useLocation();
  const navigate = useNavigate();
  const backUrl = getCloseComposeUrl(location, isChanged);

  const composeMode = useComposeMode();
  const composeType = useComposeType();

  if (isMobile === false) {
    return null;
  }

  /**
   * Handle confirmin popup modal
   */
  const handleConfirmPopup = () => {
    handleClearDraft();
    requestDraftClear();
    navigate(backUrl);
  };

  const composeTitle = getComposeTitle(composeType, nodeId, nodeType);

  const defaultProps = {
    backUrl: backUrl,
    customBackAction:
      nodeType === 'TEMPLATE' && isTemplateDraftChanged
        ? {
            dialogId: DialogId.ComposeClose,
            onConfirm: handleConfirmPopup,
            shouldConfirm: true,
          }
        : undefined,
    contentRight: null,
    title: composeTitle,
  };

  const props =
    mapping.get(composeMode)?.(location, nodeId, nodeType, composeType) ??
    defaultProps;

  return <BaseHeaderMobile {...props} />;
};

export default connect(mapStateToProps, mapDispatchToProps)(ComposeHeader);
